import React, { useState } from 'react'

import { Box, Stack, Typography } from '@mui/material'
import { Button } from '@repo/ui'

import { Input } from 'components/UI/Input'
import { OtpInput } from 'components/UI/OtpInput'
import {
  useConfirmCheckoutEmailCode,
  useSendCheckoutEmailCode,
} from 'hooks/api/pendingOwnership'

export interface CheckoutEmailRecoveryProps {
  /**
   * The address is verified. The parent RETRIES THE RESOLVE immediately — this
   * is recovery, so finishing it means finishing the thing the buyer came to
   * do, not returning them to a settings screen with a job half done.
   */
  onLinked: () => void
  /** Which step is showing, so the sheet's header can name it. */
  onStepChange?: (step: 'email' | 'code') => void
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * VRG-455 — "I ordered with a different email address."
 *
 * ## This is RECOVERY, not an account chore, and the framing is load-bearing
 *
 * The identity is the Vera ACCOUNT — a permanent user id that survives an
 * address change and is what ownership actually attaches to. An email is a
 * lookup key used ONCE, at claim time, because an order carries an address and
 * not a Vera user id. So this screen must never read as "add another email to
 * your account": that phrasing says the email IS the identity, which is exactly
 * the thing the model denies, and it turns a two-minute fix into what looks
 * like account maintenance the buyer did not ask for.
 *
 * Every string here is therefore about the PURCHASE. The address is mentioned
 * only as the thing the purchase is matched on.
 *
 * ## Nothing new is being built to verify it
 *
 * This drives the existing account-link OTP flow — a code is mailed to the
 * address entered, and confirming it nulls `otp_reference`, which is what the
 * resolve lookup reads as proof of control. Verification is not optional: an
 * unverified address would let anyone consume a stranger's entitlement by
 * typing their email.
 */
export const CheckoutEmailRecovery = ({
  onLinked,
  onStepChange,
}: CheckoutEmailRecoveryProps) => {
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [otpReference, setOtpReference] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const sendCode = useSendCheckoutEmailCode()
  const confirmCode = useConfirmCheckoutEmailCode()

  const goToStep = (next: 'email' | 'code') => {
    setStep(next)
    onStepChange?.(next)
  }

  const emailIsValid = EMAIL_PATTERN.test(email.trim())

  const handleSend = () => {
    setError(null)
    sendCode.mutate(
      { email },
      {
        onSuccess: data => {
          setOtpReference(data.otpReference)
          setCode('')
          goToStep('code')
        },
        onError: err => {
          /*
           * The server's own message is shown here, unlike everywhere else in
           * this flow, and it is a considered exception rather than a lapse.
           * `LinkAccountUseCase`'s refusals are specific and actionable in a way
           * a generic sentence cannot be — "Account already linked with
           * different provider account" is the KNOWN LIMIT of the underlying
           * mechanism (one linked address per account) and a buyer hitting it
           * needs to know that rather than be told to try again forever.
           *
           * TODO(VRG-455): the shape of that limit is worth its own copy, and
           * arguably its own product decision — a buyer with two order
           * addresses can recover one. Reported rather than papered over.
           */
          setError(err.message || "We couldn't send a code to that address.")
        },
      },
    )
  }

  const handleConfirm = (value: string) => {
    if (!otpReference) return
    setError(null)
    confirmCode.mutate(
      { otpCode: value, otpReference },
      {
        onSuccess: () => onLinked(),
        onError: err => setError(err.message || "That code didn't work. Check it and try again."),
      },
    )
  }

  if (step === 'code') {
    return (
      <Stack spacing={2}>
        <Typography sx={{ fontSize: 14, color: 'text.secondary', lineHeight: 1.5 }}>
          {/* §15: two sentences, not one em-dash-joined clause. */}
          We sent a code to {email}. Enter it to match your purchase.
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <OtpInput
            value={code}
            onChange={setCode}
            error={Boolean(error)}
            disabled={confirmCode.isPending}
            onComplete={handleConfirm}
          />
        </Box>

        {error && (
          <Typography sx={{ fontSize: 14, color: 'error.main', lineHeight: 1.5 }}>
            {error}
          </Typography>
        )}

        {/* §29: 12px between grouped controls, which is `1.5` on this app's 8px base. */}
        <Stack spacing={1.5}>
          <Button
            variant='primary'
            sx={{ width: '100%' }}
            disabled={code.length < 4 || confirmCode.isPending}
            onClick={() => handleConfirm(code)}
          >
            {confirmCode.isPending ? 'Checking…' : 'Confirm'}
          </Button>
          {/*
            §54: an exit is always available. This one goes BACK a step rather
            than out of the sheet, because the sheet's own dismissal is already
            in its header and a second one competing with it is the ambiguity
            §52 warns about.
          */}
          <Button
            variant='secondary'
            sx={{ width: '100%' }}
            disabled={confirmCode.isPending}
            onClick={() => {
              setError(null)
              goToStep('email')
            }}
          >
            Use a different address
          </Button>
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack spacing={2}>
      <Input
        label='Email used at checkout'
        type='email'
        placeholder='you@example.com'
        value={email}
        onChange={value => {
          setEmail(value)
          setError(null)
        }}
        error={Boolean(error)}
        disabled={sendCode.isPending}
      />

      {error && (
        <Typography sx={{ fontSize: 14, color: 'error.main', lineHeight: 1.5 }}>
          {error}
        </Typography>
      )}

      <Button
        variant='primary'
        sx={{ width: '100%' }}
        disabled={!emailIsValid || sendCode.isPending}
        onClick={handleSend}
      >
        {sendCode.isPending ? 'Sending…' : 'Send code'}
      </Button>
    </Stack>
  )
}

export default CheckoutEmailRecovery
